/**
 * Public Signal Draft Generator V1 - Main Generator
 * 
 * Generates public-safe LinkedIn-style draft posts from completed closeout artifacts.
 * 
 * BOUNDARIES:
 * - Advisory only: no execution authority
 * - No auto-posting capability
 * - Deterministic output only
 * - Fail closed on forbidden exposure patterns
 * - No mutation of inputs
 * 
 * INVARIANT:
 * The system may draft public signals from work completed, but it may never
 * publish automatically or disclose internal mechanics without approval.
 */

// NOTE: crypto stubbed for preview deployment (browser-safe)
// For preview, we use simple string hashing instead of Node crypto
const createHashStub = (input: string) => {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    const char = input.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(16).padStart(32, '0');
};
import {
  PublicSignalDraftInputV1,
  PublicSignalDraftV1,
  DisclosureRisk,
} from './types';
import { sanitizeCloseoutArtifact, sanitizeDocsSummary } from './closeoutSanitizer';
import { containsForbiddenTerms } from './forbiddenTermScanner';
import { generateDraftText, generateSuggestedTitle } from './draftGenerators';
import { extractSafeClaims } from './claimExtractor';

/**
 * Assess disclosure risk based on removed details and forbidden terms
 */
function assessDisclosureRisk(
  removedDetailsCount: number,
  hasForbiddenTerms: boolean
): DisclosureRisk {
  if (hasForbiddenTerms) {
    return 'high';
  }
  
  if (removedDetailsCount > 5) {
    return 'medium';
  }
  
  if (removedDetailsCount > 0) {
    return 'low';
  }
  
  return 'low';
}

/**
 * Compute deterministic draft hash
 */
function computeDraftHash(draft: Omit<PublicSignalDraftV1, 'draftHash'>): string {
  const hashInput = JSON.stringify({
    draftText: draft.draftText,
    suggestedTitle: draft.suggestedTitle,
    safeClaims: draft.safeClaims,
    removedDetails: draft.removedDetails,
    disclosureRisk: draft.disclosureRisk,
    audience: draft.audience,
    disclosureLevel: draft.disclosureLevel,
    postStyle: draft.postStyle,
  }, Object.keys(draft).sort());
  return createHashStub(hashInput);
}

/**
 * Generate public signal draft
 */
export function generatePublicSignalDraft(
  input: PublicSignalDraftInputV1
): PublicSignalDraftV1 {
  const {
    closeoutArtifact,
    docsSummary,
    audience,
    disclosureLevel,
    postStyle = 'product_progress',
  } = input;

  // Fail closed: check if minimal artifact has only forbidden terms (no safe content)
  const isMinimalArtifact = !closeoutArtifact.changes && 
                            !closeoutArtifact.invariantsPreserved && 
                            !closeoutArtifact.testResults;
  const issueHasForbiddenTerms = closeoutArtifact.issue 
    ? containsForbiddenTerms(closeoutArtifact.issue)
    : false;
  
  if (isMinimalArtifact && issueHasForbiddenTerms && disclosureLevel === 'public') {
    throw new Error(
      'Cannot generate public draft: minimal artifact contains only forbidden terms. ' +
      'Use internal or partner disclosure level, or provide artifact with safe content.'
    );
  }

  // Sanitize closeout artifact
  const { sanitizedArtifact, removedDetails: artifactRemovedDetails } = 
    sanitizeCloseoutArtifact(closeoutArtifact);

  // Sanitize docs summary if provided
  let docsRemovedDetails: any[] = [];
  if (docsSummary) {
    const docsResult = sanitizeDocsSummary(docsSummary);
    docsRemovedDetails = docsResult.removedDetails;
  }

  // Combine all removed details
  const allRemovedDetails = [
    ...artifactRemovedDetails,
    ...docsRemovedDetails,
  ];

  // Extract safe claims
  const safeClaims = extractSafeClaims(sanitizedArtifact);

  // Generate draft text
  const draftText = generateDraftText(sanitizedArtifact, audience, postStyle);

  // Generate suggested title
  const suggestedTitle = generateSuggestedTitle(sanitizedArtifact, postStyle);

  // Check if draft text contains forbidden terms
  const hasForbiddenTerms = containsForbiddenTerms(draftText);

  // Check if suggested title contains forbidden terms
  const titleHasForbiddenTerms = containsForbiddenTerms(suggestedTitle);

  // Assess disclosure risk
  const disclosureRisk = assessDisclosureRisk(
    allRemovedDetails.length,
    hasForbiddenTerms || titleHasForbiddenTerms
  );

  // Fail closed if high risk and disclosure level is public
  if (disclosureRisk === 'high' && disclosureLevel === 'public') {
    throw new Error(
      'Cannot generate public draft: high disclosure risk detected. ' +
      'Use internal or partner disclosure level, or review the closeout artifact.'
    );
  }

  // Build draft without hash
  const draftWithoutHash: Omit<PublicSignalDraftV1, 'draftHash'> = {
    draftText,
    suggestedTitle,
    safeClaims,
    removedDetails: allRemovedDetails,
    disclosureRisk,
    approvalRequired: true,
    executable: false,
    audience,
    disclosureLevel,
    postStyle,
    generatedAt: new Date().toISOString(),
  };

  // Compute deterministic hash
  const draftHash = computeDraftHash(draftWithoutHash);

  // Return complete draft
  return {
    ...draftWithoutHash,
    draftHash,
  };
}

/**
 * Validate that a draft is safe for the specified disclosure level
 */
export function validateDraftForDisclosureLevel(
  draft: PublicSignalDraftV1,
  disclosureLevel: 'public' | 'partner' | 'internal'
): { valid: boolean; reason?: string } {
  // Always require approval
  if (!draft.approvalRequired) {
    return { valid: false, reason: 'Draft must require approval' };
  }

  // Never executable
  if (draft.executable) {
    return { valid: false, reason: 'Draft must not be executable' };
  }

  // Public disclosure level requires low risk
  if (disclosureLevel === 'public' && draft.disclosureRisk !== 'low') {
    return { 
      valid: false, 
      reason: `Public disclosure requires low risk, but draft has ${draft.disclosureRisk} risk` 
    };
  }

  // Partner disclosure level allows medium risk
  if (disclosureLevel === 'partner' && draft.disclosureRisk === 'high') {
    return { 
      valid: false, 
      reason: 'Partner disclosure does not allow high risk' 
    };
  }

  // Internal allows any risk level
  return { valid: true };
}
